import { useEffect, useState } from 'react';

import { Icons } from '@/assets/icons/icons';
import { XStack, IconButton } from '@/components/ui';
import { RenameField } from '@/components/ui/RenameField/RenameField';
import { useRenameField } from '@/components/ui/RenameField/useRenameField';
import { classList } from '@/utils/cn';

interface EditableTitleProps {
  value: string;
  onEdit: (newValue: string) => void | Promise<void>;
  tooltip?: string;
  className?: string;
}

export const EditableTitle = ({
  value,
  onEdit,
  tooltip = 'Rename',
  className,
}: EditableTitleProps) => {
  const { renameFieldRef, showRenameInputField } = useRenameField();
  const [showEditButton, setShowEditButton] = useState(true);

  const handleRename = async (newValue: string) => {
    setShowEditButton(true);
    await onEdit(newValue);
  };

  const handleShowRenameField = () => {
    setShowEditButton(false);
    showRenameInputField();
  };

  useEffect(() => {
    setShowEditButton(true);
  }, [value]);

  return (
    <XStack
      className={classList(
        'flex items-center gap-0.5',
        'group text-sm',
        'hover:bg-secondary-hover rounded-lg',
        className
      )}
    >
      <RenameField
        ref={renameFieldRef}
        value={value}
        onRename={handleRename}
        onFocusRenameField={() => setShowEditButton(false)}
        className="px-1.5 py-1 rounded-lg font-medium"
        maxWidth={300}
      />

      {showEditButton && (
        <div className="-translate-x-0.5">
          <IconButton
            size="xs"
            icon={Icons.edit}
            className={classList(
              'opacity-0 touch:opacity-100 group-hover:opacity-100 focus-visible:opacity-100 group-has-[:focus-visible]:opacity-100',
              'transition-opacity duration-150'
            )}
            onClick={handleShowRenameField}
            type="button"
            tooltip={tooltip}
            tooltipOptions={{ side: 'bottom' }}
          />
        </div>
      )}
    </XStack>
  );
};
